import { formatMm } from "@/lib/format";
import type { PridanaHrana, PridanaHranaInput } from "@/api/schemas";

/**
 * Pridané hrany dielca (KOLO 169) — olepenie otvoru a pod.
 *
 * Riadok v cache nemusí mať `id` ani `material`: optimistický zápis
 * (`useUpdateDielec.onMutate`) do dielca vloží to, čo išlo do PATCH-u,
 * teda vstupný tvar. Kód, ktorý riadky číta, preto berie oba tvary.
 */
export type PridanaHranaTvar = Pick<PridanaHranaInput, "dlzka_mm"> & {
  material_id?: string | null;
  pocet?: number | null;
  popis?: string | null;
} & Partial<Pick<PridanaHrana, "id" | "material">>;

/**
 * Riadky v tvare požiadavky — PATCH zoznam vždy **nahrádza celý**.
 *
 * Odpoveď nesie aj `id` a `material`, ktoré request (`extra=forbid`)
 * neprijme, takže sa do PATCH-u nesmie poslať tak, ako prišla — týka sa
 * to kópie dielca, výmeny hrany aj návratu cez Ctrl+Z. Riadok bez
 * materiálu (položka cenníka zmizla) sa neúčtuje ani nedá uložiť.
 */
export function pridaneHranyNaVstup(
  hrany: readonly PridanaHranaTvar[] | null | undefined,
): PridanaHranaInput[] {
  return (hrany ?? [])
    .filter((h): h is PridanaHranaTvar & { material_id: string } => !!h.material_id)
    .map((h) => ({
      material_id: h.material_id,
      dlzka_mm: String(h.dlzka_mm),
      pocet: h.pocet ?? 1,
      popis: h.popis ?? "",
    }));
}

/** „hrana otvor 2× 1 000 mm" — bez popisu „hrana 1 000 mm", pri 1 ks bez „1×". */
export function popisPridanejHrany(h: PridanaHranaTvar): string {
  const pocet = h.pocet ?? 1;
  return [
    "hrana",
    (h.popis ?? "").trim(),
    pocet > 1 ? `${pocet}×` : "",
    `${formatMm(h.dlzka_mm)} mm`,
  ]
    .filter(Boolean)
    .join(" ");
}

/** Podtext pod názvom dielca v tabuľke: „+ hrana otvor 1 000 mm · + hrana 2× 300 mm". */
export function pridaneHranyPodtext(
  hrany: readonly PridanaHranaTvar[] | null | undefined,
): string | undefined {
  if (!hrany?.length) return undefined;
  return hrany.map((h) => `+ ${popisPridanejHrany(h)}`).join(" · ");
}
